import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  CircularProgress, 
  Alert, 
  Fade,
  Typography,
  Container,
  Paper,
  Button,
  Chip,
  Stack,
  Snackbar
} from '@mui/material';
import { 
  Psychology,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SnippetEditor from './SnippetEditorCore';
import ActionBar from '../common/ActionBar';
import InlineFolderSelector from '../common/InlineFolderSelector';
import AIAnalysisResults from '../common/AIAnalysisResults';
import { motion, AnimatePresence } from 'framer-motion';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useFolderContext } from '../../context/FolderContext';
import { useAI } from '../../context/AIContext';
import { useAppTheme } from '../../utils/themeUtils';
import { folderService } from '../../services/folderService';
import { calculateExpirationEpoch } from '../../utils/dateUtils';

function SnippetsMain() {
  const navigate = useNavigate();
  const { user } = useAuth() || { user: null };
  const { selectedFolderId } = useFolderContext();
  const { showAISuggestions } = useAI();
  const { colors, isDark } = useAppTheme(); // Use centralized theme like TextEditor
  
  // Editor state
  const [content, setContent] = useState('');
  const [snippetTitle, setSnippetTitle] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [expirationDays, setExpirationDays] = useState(7);
  const [tags, setTags] = useState([]);
  const [summary, setSummary] = useState('');
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  // AI Analysis State
  const [selectedAnalysisTypes, setSelectedAnalysisTypes] = useState(['all']);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisConfidence, setAnalysisConfidence] = useState(null);

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Show notification helper
  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  }, []);

  // Analysis options with corresponding prompt types
  const analysisOptions = [
    { id: 'all', label: 'All', promptType: 'ANALYZE_ALL_SNIP' },
    { id: 'summary', label: 'Summary', promptType: 'ANALYZE_SNIP' }
  ];

  // Toggle analysis type selection
  const toggleAnalysisType = (type) => {
    setSelectedAnalysisTypes(prev => {
      if (type === 'all') {
        // If 'all' is clicked, toggle between 'all' and empty
        return prev.includes('all') ? [] : ['all'];
      } else {
        // If individual type is clicked, remove 'all' and toggle the specific type
        const withoutAll = prev.filter(t => t !== 'all');
        if (withoutAll.includes(type)) {
          return withoutAll.filter(t => t !== type);
        } else {
          return [...withoutAll, type];
        }
      }
    });
  };

  // Perform AI analysis on snippet
  const performSnippetAnalysis = async () => {
    if (selectedAnalysisTypes.length === 0) {
      showNotification('Please select at least one analysis type', 'warning');
      return;
    }

    if (!content.trim()) {
      showNotification('Please add some content to your snippet before analyzing', 'warning');
      return;
    }
    
    setIsAnalyzing(true);
    setAnalysisConfidence(null);
    setAnalysisData(null);
    
    try {
      let combinedResults = [];
      let overallConfidence = null;

      // If 'all' is selected, use ANALYZE_ALL_SNIP prompt type
      if (selectedAnalysisTypes.includes('all')) {
        const response = await api.ai.analyze(content, 'ANALYZE_ALL_SNIP');
        setAnalysisResult(response.summary);
        setAnalysisData(response);
        setAnalysisConfidence(response.confidence || null);
      } else {
        // Process each selected analysis type
        const confidenceValues = [];
        const allTags = [];
        const allTitles = [];
        
        for (const analysisId of selectedAnalysisTypes) {
          const option = analysisOptions.find(opt => opt.id === analysisId);
          if (option && option.promptType) {
            const response = await api.ai.analyze(content, option.promptType);
            
            // Use first title if available, otherwise use the option label
            const title = response.titles && response.titles.length > 0 
              ? response.titles[0] 
              : option.label;
              
            combinedResults.push(`**${title}:**\n${response.summary}`);
            
            // Collect confidence values
            if (response.confidence) {
              confidenceValues.push(response.confidence);
            }
            
            // Collect tags and titles
            if (response.tags) {
              allTags.push(...response.tags);
            }
            if (response.titles) {
              allTitles.push(...response.titles);
            }
          }
        }
        
        setAnalysisResult(combinedResults.join('\n\n'));
        
        // Create combined analysis data
        const combinedAnalysisData = {
          titles: [...new Set(allTitles)], // Remove duplicates
          summary: combinedResults.join('\n\n'),
          tags: [...new Set(allTags)], // Remove duplicates
          confidence: confidenceValues.length > 0 
            ? confidenceValues.reduce((sum, conf) => sum + conf, 0) / confidenceValues.length 
            : null
        };
        
        setAnalysisData(combinedAnalysisData);
        
        // Calculate average confidence if available
        if (confidenceValues.length > 0) {
          const avgConfidence = confidenceValues.reduce((sum, conf) => sum + conf, 0) / confidenceValues.length;
          setAnalysisConfidence(avgConfidence);
        }
      }

      showNotification('Analysis completed successfully', 'success');
    } catch (error) {
      console.error('Analysis failed:', error);
      showNotification('Analysis failed. Please try again.', 'error');
      setAnalysisConfidence(null);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Edit handlers for AI analysis results
  const handleEditTitle = (newTitle) => {
    setSnippetTitle(newTitle);
    // Update analysis data
    if (analysisData) {
      setAnalysisData(prev => ({
        ...prev,
        titles: [newTitle]
      }));
    }
    showNotification('Title updated', 'success');
  };

  const handleEditTags = (newTags) => {
    setTags(newTags);
    // Update analysis data
    if (analysisData) {
      setAnalysisData(prev => ({
        ...prev,
        tags: newTags
      }));
    }
    showNotification('Tags updated', 'success');
  };

  const handleEditSummary = (newSummary) => {
    setSummary(newSummary);
    setAnalysisResult(newSummary);
    // Update analysis data
    if (analysisData) {
      setAnalysisData(prev => ({
        ...prev,
        summary: newSummary
      }));
    }
    showNotification('Summary updated', 'success');
  };

  // Copy analysis to clipboard
  const copyToClipboard = async () => {
    try {
      if (analysisResult) {
        await navigator.clipboard.writeText(analysisResult);
        showNotification('Analysis copied to clipboard!', 'success');
      }
    } catch (error) {
      showNotification('Failed to copy analysis', 'error');
    }
  };

  // Apply all AI suggestions
  const handleApplyAll = () => {
    if (analysisData) {
      // Apply title if available
      if (analysisData.titles && analysisData.titles.length > 0) {
        setSnippetTitle(analysisData.titles[0]);
      }
      
      // Apply tags if available
      if (analysisData.tags && analysisData.tags.length > 0) {
        setTags(analysisData.tags);
      }
      
      // Apply summary if available
      if (analysisData.summary) {
        setSummary(analysisData.summary);
      }
      
      showNotification('All AI suggestions applied successfully!', 'success');
    }
  };

  // Dismiss AI suggestions
  const handleDismiss = () => {
    setAnalysisResult('');
    setAnalysisData(null);
    setAnalysisConfidence(null);
    showNotification('AI suggestions dismissed', 'info');
  };
  
  // Clear error message after 5 seconds
  useEffect(() => {
    if (saveError) {
      const timer = setTimeout(() => {
        setSaveError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [saveError]);
  
  // AI callback functions for AISuggestions component (kept for compatibility)
  const handleSelectTitle = (title) => {
    setSnippetTitle(title);
  };
  
  const handleSelectTags = (tags) => {
    console.log('handleSelectTags called with:', tags);
    setTags(tags);
  };
  
  const handleSelectSummary = (summary) => {
    console.log('handleSelectSummary called with:', summary);
    setSummary(summary);
  };
  
  const handleClearTitle = () => {
    setSnippetTitle('');
  };
  
  const handleClearTags = () => {
    setTags([]);
  };
  
  const handleClearSummary = () => {
    setSummary('');
  };
  
  const handleRemoveTag = (tagToRemove) => {
    setTags(prevTags => prevTags.filter(tag => tag !== tagToRemove));
  };
  
  const handleClearAllSelections = () => {
    setSnippetTitle('');
    setSummary('');
    setTags([]);
  };
  
  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    
    try {
      // Calculate expiration date as epoch time in seconds
      const expiresAt = calculateExpirationEpoch(expirationDays);
        
      const snippetData = {
        title: snippetTitle || `New Snippet`,
        content: content || ' ', // Ensure we have at least a space character
        isPrivate,
        expiresAt,
        createdBy: user?.userId, // Use createdBy field for user ID
        createdByName: user?.name || user?.email || 'Anonymous', // Add creator name
        tags: tags,
        summary: summary,
        folderId: selectedFolderId || null // Include selected folder ID
      };
      const createdSnippet = await api.snippets.create(snippetData);
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      // Navigate to the snippet
      navigate(`/snippet/${createdSnippet.snipId}`);
    } catch (error) {
      setSaveError('Something went wrong. Try again or check your connection.');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Box 
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: isDark ? '#1a1a1a' : '#f1f3f4', // Match docs exactly
        color: colors.text,
        transition: 'all 0.3s ease',
        pt: 0,
        mt: 0,
        position: 'relative',
        top: 0,
      }}
    >
      {/* Minimal, Elegant Header - Notion Style */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ 
          margin: 0, 
          padding: 0,
          position: 'relative',
          top: 0,
          display: 'block'
        }}
      >
        {/* Improved header with better organization */}
        <Box
          sx={{
            py: 1,
            px: 2,
            mb: 2,
            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.01)',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            backdropFilter: 'blur(8px)',
          }}
        >
          {/* Top Row: Title and Primary Actions */}
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="space-between"
            mb={1.5}
          >
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{
                fontWeight: 600,
                color: colors.text,
                fontSize: '1.1rem',
                letterSpacing: '-0.01em',
              }}
            >
              P<i style={{ fontStyle: 'italic', opacity: 0.8 }}>AI</i> Snippet
            </Typography>

            {/* Primary Actions */}
            <Box display="flex" alignItems="center" gap={1}>
              <InlineFolderSelector 
                variant="compact" 
                size="small"
                placeholder="📁 Folder"
                sx={{ 
                  '& .MuiButton-root': {
                    height: 32,
                    px: 2,
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: 500,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    color: colors.text,
                    border: 'none',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                    }
                  }
                }}
              />

              <Button
                onClick={handleSave}
                disabled={saving}
                sx={{
                  height: 32,
                  px: 3,
                  borderRadius: '16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)',
                  color: isDark ? '#22c55e' : '#16a34a',
                  border: `1px solid ${isDark ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 197, 94, 0.3)'}`,
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
                    borderColor: isDark ? 'rgba(34, 197, 94, 0.5)' : 'rgba(34, 197, 94, 0.4)',
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? 'rgba(34, 197, 94, 0.05)' : 'rgba(34, 197, 94, 0.05)',
                    color: isDark ? 'rgba(34, 197, 94, 0.5)' : 'rgba(34, 197, 94, 0.6)',
                    borderColor: isDark ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>

          {/* Bottom Row: AI Analysis Controls */}
          <Box 
            display="flex" 
            alignItems="center" 
            gap={2}
            flexWrap="wrap"
          >
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontWeight: 500, minWidth: 'fit-content' }}
            >
              AI Analysis:
            </Typography>

            {/* Analysis Type Chips */}
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {analysisOptions.map((option) => (
                <Chip
                  key={option.id}
                  label={option.label}
                  onClick={() => toggleAnalysisType(option.id)}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '11px',
                    fontWeight: 500,
                    backgroundColor: selectedAnalysisTypes.includes(option.id) 
                      ? `${colors.primary}15` 
                      : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    color: selectedAnalysisTypes.includes(option.id) 
                      ? colors.primary 
                      : colors.text,
                    border: `1px solid ${selectedAnalysisTypes.includes(option.id) 
                      ? `${colors.primary}30` 
                      : 'transparent'}`,
                    '&:hover': {
                      backgroundColor: selectedAnalysisTypes.includes(option.id)
                        ? `${colors.primary}20`
                        : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                    },
                  }}
                />
              ))}
            </Box>

            {/* Analyze Button */}
            <Button
              onClick={performSnippetAnalysis}
              disabled={isAnalyzing || selectedAnalysisTypes.length === 0 || !content.trim()}
              size="small"
              sx={{
                height: 28,
                px: 2,
                borderRadius: '14px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: colors.primary,
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: colors.primaryDark || colors.primary,
                },
                '&:disabled': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  color: colors.textSecondary,
                }
              }}
            >
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </Button>
          </Box>
        </Box>
      </motion.div>

      {/* Main Content - Responsive Layout */}
      <Box display="flex" gap={3} sx={{ backgroundColor: 'transparent' }}>
        {/* Editor - Dynamic Width */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ 
            flex: analysisResult || isAnalyzing 
              ? '1 1 60%' 
              : '1 1 100%',
            backgroundColor: 'transparent'
          }}
        >
          {saveSuccess && (
            <Fade in={saveSuccess}>
              <Alert 
                severity="success" 
                sx={{ 
                  position: 'fixed',
                  top: 20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1001,
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                }}
              >
                Snippet created successfully!
              </Alert>
            </Fade>
          )}
          
          {saveError && (
            <Fade in={!!saveError}>
              <Alert 
                severity="error" 
                sx={{ 
                  position: 'fixed',
                  top: 20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1001,
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                }}
              >
                {saveError}
              </Alert>
            </Fade>
          )}

          <SnippetEditor
            content={content}
            title={snippetTitle}
            isPrivate={isPrivate}
            expirationDays={expirationDays}
            onContentChange={setContent}
            onTitleChange={setSnippetTitle}
            onPrivateChange={setIsPrivate}
            onExpirationChange={setExpirationDays}
            saving={saving}
            selectedTags={tags}
            selectedSummary={summary}
            onSelectTags={handleSelectTags}
            onSelectSummary={handleSelectSummary}
            showAISuggestions={showAISuggestions}
            // AI callback props
            onSelectTitle={handleSelectTitle}
            onClearTitle={handleClearTitle}
            onClearTags={handleClearTags}
            onClearSummary={handleClearSummary}
            onRemoveTag={handleRemoveTag}
            onClearAllSelections={handleClearAllSelections}
          />
        </motion.div>

        {/* Right Side - AI Analysis (35%) */}
        <AnimatePresence>
          {(analysisResult || isAnalyzing) && (
            <motion.div
              initial={{ opacity: 0, x: 100, width: 0 }}
              animate={{ opacity: 1, x: 0, width: '35%' }}
              exit={{ opacity: 0, x: 100, width: 0 }}
              transition={{ 
                duration: 0.6,
                ease: 'easeInOut',
                width: { duration: 0.5, ease: 'easeInOut' }
              }}
              style={{
                flex: '0 0 35%',
                minHeight: '100%'
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  position: 'sticky',
                  top: 20,
                  maxHeight: 'calc(100vh - 40px)',
                  overflow: 'hidden',
                  pl: 2, // Left padding for spacing from editor
                  pr: 3  // Right margin for better visual spacing
                }}
              >
                <AIAnalysisResults
                  analysisResult={analysisResult}
                  analysisData={analysisData}
                  isAnalyzing={isAnalyzing}
                  onAnalyze={performSnippetAnalysis}
                  onCopyAnalysis={copyToClipboard}
                  selectedAnalysisTypes={selectedAnalysisTypes}
                  confidence={analysisConfidence}
                  type="snippet"
                  canEdit={true}
                  onEditTitle={handleEditTitle}
                  onEditTags={handleEditTags}
                  onEditSummary={handleEditSummary}
                  showApplyDismiss={true}
                  onApplyAll={handleApplyAll}
                  onDismiss={handleDismiss}
                />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SnippetsMain;
